import { useCallback } from "react";

import { useSearchParams } from "react-router";

interface Options<T extends string> {
  /**
   * The value the param takes when it is absent or unrecognised. It is never
   * written to the URL, so the default state has a clean querystring.
   */
  defaultValue: NoInfer<T>;
  /**
   * The values the param may hold. Anything else in the URL — a stale link, a
   * hand-typed value — falls back to `defaultValue`. Omit for free text.
   */
  allowed?: readonly T[];
  /**
   * Whether to replace the history entry rather than push one. Defaults to
   * true, so filter twiddling doesn't fill the back button.
   */
  replace?: boolean;
}

/**
 * Holds a single piece of component state in the URL querystring, so it
 * survives a reload and can be shared as a link.
 *
 * The URL is the only source of truth — the value is derived on every render
 * rather than mirrored into `useState`, so back/forward navigation can't
 * desync it. Writes copy the params rather than mutating React Router's
 * object, and carry every other key through untouched.
 *
 * One write per tick, though: React Router hands the updater a snapshot of the
 * params as of the current render, not the live ones, so two setters called
 * from the same handler both build on the pre-update querystring and the last
 * one wins. Two keys that have to move together need a single setter that
 * writes them both.
 */
export function useSearchParamState<T extends string = string>(
  key: string,
  { defaultValue, allowed, replace = true }: Options<T>,
): readonly [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(key);
  const isValid =
    raw !== null && (!allowed || (allowed as readonly string[]).includes(raw));
  const value: T = isValid ? (raw as T) : defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue) {
            params.delete(key);
          } else {
            params.set(key, next);
          }
          return params;
        },
        { replace },
      );
    },
    [defaultValue, key, replace, setSearchParams],
  );

  return [value, setValue] as const;
}
